import clsx from "clsx";
import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { Input, type InputProps } from "../ui/Input";
import { Select, type SelectProps } from "../ui/Select";
import {
  SearchableSelect,
  type SearchableSelectProps,
} from "../ui/SearchableSelect";
import { Textarea, type TextareaProps } from "../ui/Textarea";
import { DateTimeInput, type DateTimeInputProps } from "../ui/DateTimeInput";
import { RadioGroup, type RadioGroupProps } from "../ui/RadioGroup";
import { Checkbox } from "../ui/Checkbox";
import { FormError } from "./FormError";

// Union that allows either input, select, searchableSelect, textarea, dateTime, or radioGroup props (never both)
type InputOrSelectProps<TFieldValues extends FieldValues = FieldValues> =
  | {
      type: "input";
      inputProps: InputProps;
      selectProps?: never;
      searchableSelectProps?: never;
      textareaProps?: never;
      dateTimeProps?: never;
      radioGroupProps?: never;
      checkboxProps?: never;
    }
  | {
      type: "select";
      selectProps: SelectProps;
      inputProps?: never;
      searchableSelectProps?: never;
      textareaProps?: never;
      dateTimeProps?: never;
      radioGroupProps?: never;
      checkboxProps?: never;
    }
  | {
      type: "searchableSelect";
      searchableSelectProps: SearchableSelectProps;
      inputProps?: never;
      selectProps?: never;
      textareaProps?: never;
      dateTimeProps?: never;
      radioGroupProps?: never;
      checkboxProps?: never;
    }
  | {
      type: "textarea";
      textareaProps: TextareaProps;
      inputProps?: never;
      selectProps?: never;
      searchableSelectProps?: never;
      dateTimeProps?: never;
      radioGroupProps?: never;
      checkboxProps?: never;
    }
  | {
      type: "dateTime";
      dateTimeProps: DateTimeInputProps;
      inputProps?: never;
      selectProps?: never;
      searchableSelectProps?: never;
      textareaProps?: never;
      radioGroupProps?: never;
      checkboxProps?: never;
    }
  | {
      type: "radioGroup";
      radioGroupProps: {
        control: Control<TFieldValues>;
        name: FieldPath<TFieldValues>;
        options: RadioGroupProps["options"];
        disabled?: boolean;
        layout?: "vertical" | "horizontal";
      };
      inputProps?: never;
      selectProps?: never;
      searchableSelectProps?: never;
      textareaProps?: never;
      dateTimeProps?: never;
      checkboxProps?: never;
    }
  | {
      type: "checkbox";
      checkboxProps: {
        control: Control<TFieldValues>;
        name: FieldPath<TFieldValues>;
        label?: React.ReactNode;
        disabled?: boolean;
        id?: string;
      };
      inputProps?: never;
      selectProps?: never;
      searchableSelectProps?: never;
      textareaProps?: never;
      dateTimeProps?: never;
      radioGroupProps?: never;
    };

// use `type`, not `interface`
export type FormFieldProps<TFieldValues extends FieldValues = FieldValues> =
  InputOrSelectProps<TFieldValues> & {
    label: string;
    error?: string;
    required?: boolean;
    className?: string;
    labelClassName?: string;
  };

export const FormField = <TFieldValues extends FieldValues = FieldValues>({
  label,
  type,
  inputProps,
  selectProps,
  searchableSelectProps,
  textareaProps,
  dateTimeProps,
  radioGroupProps,
  checkboxProps,
  error,
  required = false,
  className,
  labelClassName,
}: FormFieldProps<TFieldValues>) => {
  const id =
    inputProps?.id ??
    selectProps?.id ??
    searchableSelectProps?.id ??
    textareaProps?.id ??
    dateTimeProps?.id ??
    inputProps?.name ??
    selectProps?.name ??
    searchableSelectProps?.name ??
    textareaProps?.name ??
    dateTimeProps?.name ??
    (type === "radioGroup" ? String(radioGroupProps?.name) : undefined) ??
    (type === "checkbox" ? (checkboxProps?.id ?? String(checkboxProps?.name)) : undefined) ??
    undefined;

  const errorId = error ? `${id}-error` : undefined;

  return (
    <div className={clsx("flex flex-col gap-2", className)}>
      {type === "radioGroup" ? (
        <Controller
          control={radioGroupProps!.control}
          name={radioGroupProps!.name}
          render={({ field }) => (
            <>
              <label htmlFor={id} className={clsx("text-sm font-medium text-gray-700", labelClassName)}>
                {label}
                {required ? <span className="text-red-500"> *</span> : null}
              </label>
              <fieldset
                className="flex flex-col gap-2"
                aria-describedby={errorId}
                disabled={radioGroupProps!.disabled}
              >
                <RadioGroup
                  name={field.name}
                  value={field.value || ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  options={radioGroupProps!.options}
                  disabled={radioGroupProps!.disabled}
                  layout={radioGroupProps!.layout}
                  id={id}
                  aria-invalid={!!error}
                  aria-describedby={errorId}
                />
              </fieldset>
            </>
          )}
        />
      ) : type === "checkbox" ? (
        <Controller
          control={checkboxProps!.control}
          name={checkboxProps!.name}
          render={({ field }) => (
            <>
              {label ? (
                <span className={clsx("text-sm font-medium text-gray-700", labelClassName)}>
                  {label}
                  {required ? <span className="text-red-500"> *</span> : null}
                </span>
              ) : null}
              <div aria-describedby={errorId}>
                <Checkbox
                  name={field.name}
                  checked={Boolean(field.value)}
                  onChange={(checked) => field.onChange(checked)}
                  onBlur={field.onBlur}
                  label={checkboxProps!.label}
                  disabled={checkboxProps!.disabled}
                  id={id}
                  aria-invalid={!!error}
                  aria-describedby={errorId}
                />
              </div>
            </>
          )}
        />
      ) : (
        <>
          <label htmlFor={id} className={clsx("text-sm font-medium text-gray-700", labelClassName)}>
            {label}
            {required ? <span className="text-red-500"> *</span> : null}
          </label>

          {type === "input" && <Input {...inputProps!} id={id} />}
          {type === "select" && <Select {...selectProps!} id={id} />}
          {type === "searchableSelect" && (
            <SearchableSelect {...searchableSelectProps!} id={id} />
          )}
          {type === "textarea" && <Textarea {...textareaProps!} id={id} />}
          {type === "dateTime" && (
            <DateTimeInput {...dateTimeProps!} id={id} required={required} />
          )}
        </>
      )}

      {error && <FormError id={errorId} message={error} />}
    </div>
  );
};
